import React, { useState, useMemo, useEffect, useRef } from 'react';
import Immutable from 'immutable';
import cx from 'classnames';
import Selectro from 'components/common/Selectro';
import CommonCheckbox from 'components/common/Checkbox';
import CommonColorPicker from 'components/common/ColorPicker';
import {
  Text as CommonTextField,
  Number as CommonNumberField,
} from '@aha-app/react-in-place';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import { uniqueId } from 'lodash';
import AhaBaseEventListener from 'components/common/AhaBaseEventListener';
import { AhaButton, AhaSlider } from 'components/common/reactifyWebComponent';
import { setBackgroundAndGridColors } from '../../custom_pivot_chart/helpers';
import {
  setLocalStoragePreference,
  getLocalStoragePreference,
} from 'javascripts/utils';
import { decodeColor } from '@aha-app/aha-shared';

const fontSizes = [10, 12, 14, 16, 18, 20, 24, 30, 36];

function safeChildrenMap(children, callback) {
  children = React.Children.toArray(children).filter(Boolean);
  return React.Children.map(children, callback);
}

const SectionContext = React.createContext({
  id: '',
  open: false,
  setOpen: _open => {},
  persistToLocalstorage: false,
});

export function useSection() {
  const context = React.useContext(SectionContext);

  if (!context) {
    throw new Error(`useSection must be used within a SectionContext`);
  }

  return context;
}

// If both a prefix and a suffix are provided, then it is assumed that *YOU the developer* are responsible for ensuring that this id is unique.
// If only a prefix is provided, then this hook will generate a unique id for you .
function useUniqueId(prefix, suffix = '') {
  return useMemo(
    () => (suffix ? `${prefix}-${suffix}` : uniqueId(prefix)),
    [prefix, suffix]
  );
}

export /**
 * @typedef SectionProps
 * @prop {*=} defaultOpen
 * @prop {*=} forceOpen
 * @prop {*=} withDivider
 * @prop {*=} localStorageUUID
 * @prop {*=} persistToLocalstorage
 */
/**
 * @type {React.FC<SectionProps>}
 */
function Section({
  forceOpen = false,
  defaultOpen = false,
  withDivider = true,
  children,
  localStorageUUID = '',
  persistToLocalstorage = false,
}) {
  const id = useUniqueId('configuration-section', localStorageUUID);
  defaultOpen =
    forceOpen || localStorageUUID
      ? Boolean(
          // There is a `Number` function defined in this file, which overrides the easy reference to `window.Number` that we actually want here.
          window.Number(getLocalStoragePreference(id) || defaultOpen || '0')
        )
      : defaultOpen;

  const [open, setOpen] = useState(defaultOpen);

  return (
    <SectionContext.Provider
      value={{ id, open, setOpen, persistToLocalstorage }}
    >
      <div
        className={cx('custom-chart-sidebar__section', {
          'custom-chart-sidebar__section--closed': !open,
        })}
      >
        {children}
        {withDivider && <Divider />}
      </div>
    </SectionContext.Provider>
  );
}

export /**
 * @typedef HeaderProps

 */
/**
 * @type {React.FC<HeaderProps>}
 */
function Header({ children }) {
  const { id: sectionId, setOpen, open, persistToLocalstorage } = useSection();

  return (
    <div
      id={sectionId}
      role='region'
      aria-labelledby={sectionId}
      className='custom-chart-sidebar__header'
      onClick={() => {
        setOpen(!open);
        if (persistToLocalstorage) {
          // Because we're working with the "old" value of open, we need to invert it here.
          setLocalStoragePreference(sectionId, open ? '0' : '1');
        }
      }}
    >
      {children}{' '}
      <span className='custom-chart-sidebar__section-toggle'>
        <i className={`fa-regular fa-angle-${open ? 'down' : 'right'}`} />
      </span>
    </div>
  );
}

export /**
 * @typedef SubHeaderProps

 */
/**
 * @type {React.FC<SubHeaderProps>}
 */
function SubHeader({ children }) {
  return (
    <div className='custom-chart-sidebar__header custom-chart-sidebar__header--subheader'>
      {children}
    </div>
  );
}

export /**
 * @typedef ControlsProps

 */
/**
 * @type {React.FC<ControlsProps>}
 */
function Controls({ children }) {
  const { open } = useSection();
  if (!open) return null;

  return (
    <div className='custom-chart-sidebar__controls m0 mb1'>{children}</div>
  );
}

export /**
 * @typedef LabeledControlProps
 * @prop {*=} title
 * @prop {*=} alignRight
 * @prop {*=} inline
 * @prop {*=} alignTop
 * @prop {*=} onClickTitle
 */
/**
 * @type {React.FC<LabeledControlProps>}
 */
function LabeledControl({
  title,
  children,
  alignRight = false,
  inline = false,
  alignTop = false,
  onClickTitle,
  classNameTitle,
}) {
  const id = useUniqueId('sidebar-control');
  const { id: sectionId } = useSection();

  return (
    <>
      <span
        id={id}
        className={cx('custom-chart-control__title ', {
          [classNameTitle]: true,
          'custom-chart-control--align-top': alignTop,
        })}
        onClick={onClickTitle}
      >
        {title}
      </span>
      <span
        role='group'
        aria-labelledby={`${sectionId} ${id}`}
        className={cx('custom-chart-control__content', {
          'custom-chart-control__content--inline': inline,
          'custom-chart-control__content--align-right': alignRight,
        })}
      >
        {children}
      </span>
    </>
  );
}

export /**
 * @typedef InlineControlProps

 */
/**
 * @type {React.FC<InlineControlProps>}
 */
function InlineControl({ children }) {
  return (
    <span
      className={`
        custom-chart-control__content
        custom-chart-control__content--inline
        custom-chart-control__content--single-column
      `}
    >
      {children}
    </span>
  );
}

export /**
 * @typedef DividerProps
 * @prop {*=} type
 */
/**
 * @type {React.FC<DividerProps>}
 */
function Divider({ type = null }) {
  return (
    <div
      className={cx('custom-chart-sidebar__divider', {
        [`custom-chart-sidebar__divider--${type}`]: type,
      })}
    />
  );
}

export /**
 * @typedef SelectionProps
 * @prop {*=} options
 * @prop {*=} configurationKey
 * @prop {*=} disabled
 * @prop {*=} singleColumn
 */
/**
 * @type {React.FC<SelectionProps>}
 */
function Selection({
  options,
  configurationKey,
  disabled = false,
  singleColumn = false,
}) {
  const { set, get } = useReportConfiguration();
  const selectedValue = get(configurationKey);

  const selectedOption =
    (selectedValue !== undefined &&
      options.find(([, value]) => selectedValue === value)) ||
    options[0];

  const selected = Immutable.List([selectedOption]);

  return (
    <span
      className={cx('custom-chart-control__selectro ', {
        'custom-chart-control__content--single-column': singleColumn,
      })}
    >
      <Selectro
        selectedElements={selected}
        defaultLabel={'Select a layout'}
        select={({ text }) => {
          const [, value] = options.find(([key]) => key === text);
          console.debug(options, text, value);
          set(configurationKey, value);
        }}
        multiple={false}
        fetchOptions={Immutable.fromJS(options)}
        disabled={disabled}
      />
    </span>
  );
}

export /**
 * @typedef FetchedSelectionProps
 * @prop {*=} selectedElements
 * @prop {*=} placeholder
 * @prop {*=} fetchUrl
 * @prop {*=} nil
 * @prop {*=} configurationKey
 * @prop {*=} onChange
 */
/**
 * @type {React.FC<FetchedSelectionProps>}
 */
function FetchedSelection({
  selectedElements,
  placeholder,
  fetchUrl,
  nil,
  configurationKey,
  onChange = null,
}) {
  const { set } = useReportConfiguration();

  const setValue = value => {
    set(configurationKey, value);

    if (onChange) {
      onChange(value);
    }
  };

  return (
    <span className='custom-chart-control__selectro'>
      <Selectro
        multiple={false}
        headersClickable={false}
        fetchUrl={fetchUrl}
        prescript=''
        nil={nil}
        placeholder={placeholder}
        selectedElements={selectedElements || []}
        select={option => {
          setValue(option.value);
        }}
        showSearch
      />
    </span>
  );
}

export /**
 * @typedef ToggleGroupProps
 * @prop {*=} configurationKey
 * @prop {*=} onChange
 * @prop {*=} disabled
 */
/**
 * @type {React.FC<ToggleGroupProps>}
 */
function ToggleGroup({
  configurationKey,
  onChange = null,
  disabled = false,
  children,
}) {
  const { set, get } = useReportConfiguration();
  const selectedValue = get(configurationKey);

  return (
    <fieldset
      className='custom-chart-control__toggle-group'
      disabled={disabled}
    >
      {safeChildrenMap(children, child =>
        React.cloneElement(child, {
          selectedValue,
          configurationKey,
          onChange: value => {
            if (value === selectedValue) return;

            if (onChange) {
              onChange(value);
            } else {
              set(configurationKey, value);
            }
          },
        })
      )}
    </fieldset>
  );
}

export /**
 * @typedef ToggleOptionProps
 * @prop {*=} configurationKey
 * @prop {*=} selectedValue
 * @prop {*=} value
 * @prop {*=} onChange
 */
/**
 * @type {React.FC<ToggleOptionProps>}
 */
function ToggleOption({
  children,
  configurationKey,
  selectedValue,
  value,
  onChange,
}) {
  const selected = selectedValue === value;
  const inputKey = `${configurationKey}_${value}`;

  return (
    <label className='custom-chart-control__icon-toggle'>
      <input
        type='radio'
        name={inputKey}
        checked={selected}
        onChange={() => onChange(value)}
      />
      {children}
    </label>
  );
}

/**
 * @typedef ColorPickerProps
 * @prop {string} configurationKey
 * @prop {string} defaultColor
 * @prop {*=} options
 * @prop {boolean} disabled
 */
/**
 * @type {React.FC<ColorPickerProps>}
 */
function ColorPicker({
  configurationKey,
  onChange = null,
  defaultColor = '',
  options = {},
  disabled = false,
}) {
  const { set, get } = useReportConfiguration();
  let currentColor = get(configurationKey) || defaultColor;

  if (currentColor !== 'transparent') {
    currentColor = decodeColor(currentColor);
  }

  const colorPickerRef = useRef(null);

  useEffect(() => {
    const el = colorPickerRef.current;
    if (!el) return;
    const handler = ({ detail }) => {
      let { value } = detail;
      // Make sure value is a color code or 'transparent'
      if (value !== 'transparent') {
        value = `#${value}`;
      }
      if (onChange) {
        onChange(value);
      } else {
        set(configurationKey, value);
      }
    };
    el.addEventListener('aha-color-picker:change', handler);
    return () => el.removeEventListener('aha-color-picker:change', handler);
  }, [configurationKey, onChange, set]);

  return (
    <div
      className={cx('custom-chart-control__color-picker', {
        'custom-chart-control__color-picker--disabled': disabled,
      })}
    >
      <CommonColorPicker
        color={currentColor}
        onChangeColor={value => {
          if (onChange) onChange(value);
          else set(configurationKey, value);
        }}
        disabled={disabled}
        options={options}
      >
        <div
          className='custom-chart-control__color-picker-selection'
          style={{ background: currentColor }}
        />
      </CommonColorPicker>
    </div>
  );
}

export /**
 * @typedef CheckboxProps
 * @prop {string} configurationKey
 * @prop {boolean} disabled
 * @prop {*=} children
 * @prop {*=} onClick
 */
/**
 * @type {React.FC<CheckboxProps>}
 */
function Checkbox({
  configurationKey,
  children = null,
  disabled = false,
  onClick,
}) {
  const { set, get } = useReportConfiguration();
  const value = get(configurationKey);
  const checked = value === '0' ? false : Boolean(value);

  return (
    <CommonCheckbox
      checked={checked}
      disabled={disabled}
      onChange={e => {
        if (onClick) {
          onClick(e.target.checked);
        }
        set(configurationKey, e.target.checked);
      }}
    >
      {children}
    </CommonCheckbox>
  );
}

export /**
 * @typedef InlineTextProps

 */
/**
 * @type {React.FC<InlineTextProps>}
 */
function InlineText({ children, ...props }) {
  return (
    <InlineReactInPlaceField type='text' {...props}>
      {children}
    </InlineReactInPlaceField>
  );
}

export /**
 * @typedef InlineNumberProps

 */
/**
 * @type {React.FC<InlineNumberProps>}
 */
function InlineNumber({ children, ...props }) {
  return (
    <InlineReactInPlaceField type='number' {...props}>
      {children}
    </InlineReactInPlaceField>
  );
}

/**
 * @typedef InlineReactInPlaceFieldProps
 * @prop {*=} type
 * @prop {*=} configurationKey
 */
/**
 * @type {React.FC<InlineReactInPlaceFieldProps>}
 */
function InlineReactInPlaceField({
  type,
  configurationKey,
  children,
  ...props
}) {
  const id = useUniqueId('field-label');
  const { id: sectionId } = useSection();

  return (
    <div>
      <span className='custom-chart-control__react-in-place-label' id={id}>
        {children}
      </span>

      <div
        style={{ display: 'contents' }}
        aria-labelledby={`${sectionId} ${id}`}
      >
        <ReactInPlaceField
          type={type}
          configurationKey={configurationKey}
          inline
          {...props}
        />
      </div>
    </div>
  );
}

export /**
 * @typedef TextProps
 * @prop {boolean} exampleProp
 */
/**
 * @type {React.FC<TextProps>}
 */
function Text(props) {
  return <ReactInPlaceField type='text' {...props} />;
}

export /**
 * @typedef NumberProps
 * @prop {boolean} exampleProp
 */
/**
 * @type {React.FC<NumberProps>}
 */
function Number(props) {
  return <ReactInPlaceField type='number' {...props} />;
}

/**
 * @typedef ReactInPlaceFieldProps
 * @prop {*=} type
 * @prop {*=} configurationKey
 * @prop {*=} inline
 * @prop {*=} size
 */
/**
 * @type {React.FC<ReactInPlaceFieldProps>}
 */
function ReactInPlaceField({
  type,
  configurationKey,
  inline = false,
  size = null,
}) {
  const ReactInPlaceComponent = {
    number: CommonNumberField,
    text: CommonTextField,
  }[type];

  const { set, get } = useReportConfiguration();
  const currentValue = get(configurationKey);

  return (
    <ReactInPlaceComponent
      class={cx(
        'react-in-place',
        'custom-chart-control__react-in-place',
        `custom-chart-control__react-in-place--${type}`,
        {
          'custom-chart-control__react-in-place--inline': inline,
          [`custom-chart-control__react-in-place--${size}`]: size,
        }
      )}
      value={currentValue}
      onChange={value => set(configurationKey, value)}
    />
  );
}

export /**
 * @typedef ResetProps
 * @prop {*=} label
 * @prop {*=} configurationKeys
 * @prop {*=} className
 * @prop {*=} disabled
 */
/**
 * @type {React.FC<ResetProps>}
 */
function Reset({
  label,
  configurationKeys,
  className = '',
  disabled = false,
  children = null,
}) {
  const { reset } = useReportConfiguration();

  return (
    <a
      role='button'
      className={cx('custom-chart-control__reset', className, {
        'custom-chart-control__reset--disabled': disabled,
      })}
      onClick={e => {
        e.preventDefault();
        reset(configurationKeys);
      }}
      aria-label={label}
    >
      {children || <i className='fa-regular fa-undo' />}
    </a>
  );
}

/**
 * Slider component based on aha-slider. TODO fix this doc
 * @typedef SliderProps
 * @prop {string} configurationKey - key in default config this component will affect
 * @prop {*=} props - optional config for things like step, etc. TODO should this be props?
 * @prop {boolean} disabled - enable slider based on pie chart type
 */
/**
 * @type {React.FC<SliderProps>}
 */
function Slider({ configurationKey, props, disabled }) {
  const { set, get } = useReportConfiguration();
  const startValue = get(configurationKey);
  props.startValue = startValue;

  return (
    <AhaBaseEventListener
      slot='trigger'
      Component='div'
      className={cx('custom-chart-control__slider', {
        'custom-chart-control__slider--disabled': disabled,
      })}
      eventListeners={{
        'aha-slider:clicked': event => {
          const value =
            parseInt(
              event.srcElement.selectedTickValue ||
                event.srcElement.inputSliderValue
            ) || 0;
          set(configurationKey, value);
        },
      }}
    >
      <AhaSlider {...props} className='custom-chart-control__slider-label'>
        <div slot='label' />
        <div slot='units'>%</div>
      </AhaSlider>
    </AhaBaseEventListener>
  );
}

/**
 * @typedef MenuProps
 * @prop {string} configurationKey
 * @prop {Array} options
 * @prop {*=} defaultValue
 * @prop {(value) => void} onChange - value will be one of options prop
 * @prop {bool} disabled
 */
/**
 * @type {React.FC<MenuProps>}
 */
function Menu({
  configurationKey,
  options,
  defaultValue,
  onChange = null,
  disabled = false,
}) {
  const { set, get } = useReportConfiguration();
  const currentValue = get(configurationKey) || defaultValue;
  const inputProps = disabled ? { disabled } : {};

  return (
    <aha-menu label={currentValue} {...inputProps}>
      <aha-menu-control>
        {options.map(value => (
          <aha-menu-item
            key={value}
            onClick={() =>
              onChange ? onChange(value) : set(configurationKey, value)
            }
          >
            {value}
          </aha-menu-item>
        ))}
      </aha-menu-control>
    </aha-menu>
  );
}

/**
 * @typedef ToggleButtonProps
 * @prop {string} configurationKey
 * @prop {string | number} value - value passed when this element is clicked
 * @prop {string | number} defaultValue
 * @prop {*=} children - content to display
 * @prop {(value: string | number) => void} onClick - value will be whatever was passed in for value prop
 * @prop {boolean} disabled
 */
/**
 * @type {React.FC<ToggleButtonProps>}
 */
function ToggleButton({
  configurationKey,
  value,
  defaultValue,
  children,
  onClick,
  disabled = false,
}) {
  const { get } = useReportConfiguration();
  const currentValue = get(configurationKey) || defaultValue;
  const inputProps = disabled ? { disabled } : {};

  return (
    <AhaButton
      className={cx('custom-chart-control__button', {
        'custom-chart-control__button--active': currentValue === value,
      })}
      onClick={() => {
        onClick(currentValue);
      }}
      {...inputProps}
    >
      {children}
    </AhaButton>
  );
}

/**
 * @typedef TextFormatProps
 * @prop string configurationKey
 * @prop {number} defaultSize - font size in px
 * @prop {string} defaultColor - font color
 * @prop {string} defaultFontWeight - should be a css font-weight value
 * @prop {string} defaultFontStyle - should be a css font-style value
 * @prop {boolean} disabled - whether this should be clickable or grayed out
 */
/**
 * @type {React.FC<TextFormatProps>}
 */
function TextFormat({
  configurationKey,
  defaultSize,
  defaultColor,
  defaultFontWeight,
  defaultFontStyle,
  disabled = false,
}) {
  const { set } = useReportConfiguration();

  return (
    <div
      className={cx('custom-chart-control__text-format', {
        'custom-chart-control__text-format--disabled': disabled,
      })}
    >
      <Menu
        configurationKey={[configurationKey, 'fontSize']}
        options={fontSizes.map(size => `${size}px`)}
        defaultValue={`${defaultSize}px`}
        disabled={disabled}
      />
      <ToggleButton
        configurationKey={[configurationKey, 'fontWeight']}
        value='bold'
        defaultValue={defaultFontWeight}
        onClick={current => {
          set(
            [configurationKey, 'fontWeight'],
            current === 'bold' ? 'normal' : 'bold'
          );
        }}
        disabled={disabled}
      >
        <aha-icon icon={'fa-regular fa-bold'} />
      </ToggleButton>
      <ToggleButton
        configurationKey={[configurationKey, 'fontStyle']}
        value='italic'
        defaultValue={defaultFontStyle}
        onClick={current => {
          set(
            [configurationKey, 'fontStyle'],
            current === 'italic' ? 'normal' : 'italic'
          );
        }}
        disabled={disabled}
      >
        <aha-icon icon={'fa-regular fa-italic'} />
      </ToggleButton>
      <ColorPicker
        configurationKey={[configurationKey, 'color']}
        defaultColor={defaultColor}
        disabled={disabled}
      />
    </div>
  );
}

function BackgroundColorControl() {
  const { merge } = useReportConfiguration();

  return (
    <LabeledControl title='Background'>
      <ColorPicker
        configurationKey='backgroundColor'
        onChange={color => setBackgroundAndGridColors(color, merge)}
        options={{ noFill: 'Clear background' }}
      />
    </LabeledControl>
  );
}

export { BackgroundColorControl, ColorPicker, Slider, TextFormat, fontSizes };
